import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Mail, Lock, User, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { login: googleLogin, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isAdmin = searchParams.get("admin") === "true";
  const redirectTo = searchParams.get("redirect") || (isAdmin ? "/admin" : "/dashboard");

  const [mode, setMode] = useState(isAdmin ? "login" : "login"); // login or register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  // Show error from failed Google auth redirect
  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
      // Clear the state so it doesn't show again on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "register") {
        await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
        await checkAuth(); // Fetch full user data with tier
        toast.success("Account created! Welcome to NaturalOne-Chronicles.");
        navigate(redirectTo, { replace: true });
      } else {
        await axios.post(`${API}/auth/login-email`, { email, password }, { withCredentials: true });
        await checkAuth(); // Fetch full user data with tier
        toast.success("Welcome back!");
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(" ") : "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center"
      data-testid="login-page"
    >
      {/* Background */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/207130/pexels-photo-207130.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.2)"
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Back */}
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-muted-foreground mb-6"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        {/* Card */}
        <div className="bg-card/95 backdrop-blur-sm border border-border/50 rounded-lg p-8">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-6 justify-center">
            {isAdmin ? (
              <ShieldCheck className="w-8 h-8 text-primary" />
            ) : (
              <BookOpen className="w-8 h-8 text-primary" />
            )}
            <h1 className="text-xl font-serif font-bold">
              {isAdmin ? "Admin Login" : "NaturalOne-Chronicles"}
            </h1>
          </div>

          {/* Toggle */}
          {!isAdmin && (
            <div className="flex mb-6 bg-muted/30 rounded-sm p-1">
              <button
                onClick={() => setMode("login")}
                className={`flex-1 py-2 text-sm rounded-sm transition-colors ${
                  mode === "login" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="toggle-login"
              >
                Sign In
              </button>
              <button
                onClick={() => setMode("register")}
                className={`flex-1 py-2 text-sm rounded-sm transition-colors ${
                  mode === "register" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="toggle-register"
              >
                Create Account
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Display Name</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your adventurer name"
                    className="pl-10"
                    data-testid="register-name"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isAdmin ? "Admin email" : "your@email.com"}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Min 6 characters" : "Your password"}
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="submit-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isAdmin ? "Login to Admin Panel" : mode === "register" ? "Create Account" : "Sign In"}
            </Button>
          </form>

          {/* Divider + Google */}
          {!isAdmin && (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <Button
                variant="outline"
                onClick={() => googleLogin(redirectTo)}
                className="w-full"
                data-testid="google-login-btn"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          {isAdmin ? "Admin access only" : "By signing up you agree to our terms of service"}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
